import React, { useCallback } from 'react';

import { di } from 'react-magnetic-di';

import { isJiraCreatableAction } from '@atlassian/conversation-assistant-action-helpers/utils/jira-actions';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import { type ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';
import { JiraCreateWithRovoPreviewCard } from '@atlassian/conversation-assistant-preview-card/jira-create-with-rovo-preview-card';
import type { MessageActionResolutions } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import {
	isStoreAssistant,
	type StoreAssistant,
	type StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import {
	useStagingAreaActions,
	useStagingAreaState,
} from '@atlassian/conversation-assistant-store/controllers/staging-area';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

// Returns the id of the last assistant message that contains Jira creatable actions, ignoring
// other unrelated messages.
const getLastCreatableActionMessageId = (messages?: StoreChatItem[]): string => {
	const assistantMessagesWithCreatableActions: StoreAssistant[] = (messages?.filter((msg) => {
		if (!isStoreAssistant(msg)) {
			return false;
		}
		const messageActions = msg.content?.message?.message?.actions;
		return Boolean(messageActions?.[0] && isJiraCreatableAction(messageActions[0]));
	}) ?? []) as StoreAssistant[];

	const lastMessageWithCreatableActions =
		assistantMessagesWithCreatableActions.length > 0
			? assistantMessagesWithCreatableActions[assistantMessagesWithCreatableActions.length - 1]
			: undefined;

	return lastMessageWithCreatableActions?.content?.message?.message?.id ?? '';
};

export const JiraSmartIssueCreateAllFieldAction = ({
	actions,
	cloudId,
	conversationId,
	messageId,
	isLastMessage,
	productKey,
	onCancel,
	onCustomDataActioned,
	resolveActionsBulk,
}: ActionRendererFor<typeof ActionKeys.JiraSmartIssueCreateAllField>): React.JSX.Element | null => {
	di(JiraCreateWithRovoPreviewCard);

	const { sendAnalyticsEvent } = useAnalytics();
	const { messageIdSelectedForPreview, invocationIdSelectedForPreview } = useStagingAreaState();
	const { setMessageIdSelectedForPreview, setInvocationIdSelectedForPreview, setStagingAreaMode } =
		useStagingAreaActions();

	const [chatMessages] = useChatMessages({ conversationId: conversationId ?? '' });

	const firstInvocationId = actions[0]?.invocationId;

	// Derive "is this the active AIFC message" from chat messages rather than the naive isLastMessage
	// prop. Resolving a sibling action on the same message (e.g. an Update work items action) appends
	// an optimistic assistant response, which would otherwise flip the prop to false and incorrectly
	// disable this card. We fall back to the prop when messages aren't available.
	const lastCreatableActionMessageId = getLastCreatableActionMessageId(chatMessages);
	const effectiveIsLastMessage = lastCreatableActionMessageId
		? lastCreatableActionMessageId === messageId
		: Boolean(isLastMessage);

	const isSelected =
		messageIdSelectedForPreview === messageId ||
		(!!invocationIdSelectedForPreview && invocationIdSelectedForPreview === firstInvocationId);
	// The preview card is interactive only when the staging area/canvas isn't open and this is the
	// most recent message that can create work items.
	const isEnabled = messageIdSelectedForPreview === null && effectiveIsLastMessage;
	const isStagingArea = !!messageIdSelectedForPreview || !!invocationIdSelectedForPreview;

	const resolveActions = useCallback(
		(
			_actionKey: string,
			actionResolution: MessageActionResolutions,
			invocationIds: string[],
			invocationIdToCreatedIssueKey?: Record<string, string>,
		) => {
			invocationIds.forEach((invocationId) => {
				const action = actions.find((a) => a.invocationId === invocationId);
				if (!action) {
					return;
				}
				if (actionResolution === 'cancel') {
					onCancel(action);
					return;
				}
				onCustomDataActioned(
					action,
					{
						...action.data,
						...(invocationIdToCreatedIssueKey
							? { createdIssueKey: invocationIdToCreatedIssueKey[invocationId] }
							: {}),
					},
					undefined,
					true, // Skip the optimistic assistant response
				);
			});
		},
		[actions, onCancel, onCustomDataActioned],
	);

	const onPreviewClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'createWorkItemsAifcPreviewOpened',
				attributes: {
					itemCount: actions.length,
					isSingleWorkItem: actions.length === 1,
					isStagingArea,
				},
			});

			if (firstInvocationId) {
				setInvocationIdSelectedForPreview(firstInvocationId);
			}
			setMessageIdSelectedForPreview(messageId);
			setStagingAreaMode('modal', 'rovoChatPreviewCard');
		},
		[
			actions.length,
			firstInvocationId,
			isStagingArea,
			messageId,
			sendAnalyticsEvent,
			setInvocationIdSelectedForPreview,
			setMessageIdSelectedForPreview,
			setStagingAreaMode,
		],
	);

	if (actions.length === 0) {
		return null;
	}

	return (
		<AtlassianRelayEnvironmentProvider>
			<JiraCreateWithRovoPreviewCard
				product={productKey}
				actions={actions}
				messageId={messageId}
				cloudId={cloudId}
				conversationId={conversationId ?? ''}
				isSelected={isSelected}
				isEnabled={isEnabled}
				isLastMessage={effectiveIsLastMessage}
				onPreviewClick={onPreviewClick}
				resolveActions={resolveActionsBulk ?? resolveActions}
				isStagingArea={isStagingArea}
			/>
		</AtlassianRelayEnvironmentProvider>
	);
};
